import React, { useEffect, useRef, useState } from "react";
import AppFooter from "../components/layout/AppFooter";
import ChatWidget from "../components/ui/ChatWidget";

/* ── Fade-in on scroll hook ── */
function useFadeIn(threshold = 0.15) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, visible];
}

function FadeIn({ children, delay = 0, style = {}, className = "" }) {
    const [ref, visible] = useFadeIn(0.12);
    return (
        <div
            ref={ref}
            className={className}
            style={{
                ...style,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(28px)",
                transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
            }}
        >
            {children}
        </div>
    );
}

/* ── Animated counter ── */
function AnimatedStat({ value, suffix = "", label }) {
    const [ref, visible] = useFadeIn(0.2);
    const [display, setDisplay] = useState("0");
    const numMatch = value.match(/[\d.]+/);
    const prefix = value.replace(/[\d.]+.*/, "");

    useEffect(() => {
        if (!visible || !numMatch) { setDisplay(value); return; }
        const target = parseFloat(numMatch[0]);
        const duration = 1200;
        const start = performance.now();
        const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(eased * target);
            setDisplay(current.toString());
            if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [visible]); // eslint-disable-line

    return (
        <div ref={ref} style={s.statItem}>
            <div style={s.statVal}>
                {prefix}<span style={s.statAccent}>{numMatch ? display : value}</span>{suffix}
            </div>
            <div style={s.statLabel}>{label}</div>
        </div>
    );
}

/* ── Partner logos as SVG ── */
const PartnerLogos = () => (
    <div style={s.partnersSection}>
        <FadeIn>
            <p style={s.partnersLabel}>Technologies & partenaires</p>
        </FadeIn>
        <div style={s.partnersGrid}>
            {[
                {
                    name: "OpenAI", svg: (
                        <svg width="100" height="24" viewBox="0 0 120 24" fill="none">
                            <path d="M19.21 11.72c.39-1.16.39-2.43 0-3.59a5.85 5.85 0 00-6.34-3.87A5.88 5.88 0 008.5 1.44 5.85 5.85 0 002.16 5.3a5.88 5.88 0 00-1.94 4.23 5.85 5.85 0 00.73 5.03 5.88 5.88 0 002.73 2.45 5.85 5.85 0 003.61.42 5.88 5.88 0 004.37 2.82 5.85 5.85 0 006.34-3.87 5.88 5.88 0 003.8-1.48 5.85 5.85 0 001.41-3.18z" fill="rgba(255,255,255,0.7)" />
                            <text x="28" y="17" fill="rgba(255,255,255,0.7)" style={{ fontSize: 14, fontWeight: 600, fontFamily: "system-ui" }}>OpenAI</text>
                        </svg>
                    )
                },
                {
                    name: "Anthropic", svg: (
                        <svg width="110" height="24" viewBox="0 0 130 24" fill="none">
                            <path d="M8 20L14 4h3l6 16h-3l-1.4-4H12.4L11 20H8zm5.2-7h5.6L16 6.5h-.1L13.2 13z" fill="rgba(255,255,255,0.7)" />
                            <text x="30" y="17" fill="rgba(255,255,255,0.7)" style={{ fontSize: 14, fontWeight: 600, fontFamily: "system-ui" }}>Anthropic</text>
                        </svg>
                    )
                },
                {
                    name: "Stripe", svg: (
                        <svg width="80" height="24" viewBox="0 0 100 24" fill="none">
                            <path d="M13 4c-2.7 0-4.5 1.4-4.5 3.6 0 2.8 4.1 3.2 4.1 4.8 0 .6-.5 1.1-1.4 1.1-1.3 0-2.8-.8-2.8-.8L7.5 15s1.8 1 3.8 1c2.8 0 4.7-1.4 4.7-3.7 0-3-4.1-3.4-4.1-4.9 0-.5.4-1 1.3-1 1 0 2.3.5 2.3.5l.8-2.3S15 4 13 4z" fill="rgba(255,255,255,0.7)" />
                            <text x="22" y="17" fill="rgba(255,255,255,0.7)" style={{ fontSize: 14, fontWeight: 600, fontFamily: "system-ui" }}>Stripe</text>
                        </svg>
                    )
                },
                {
                    name: "PayPal", svg: (
                        <svg width="86" height="24" viewBox="0 0 106 24" fill="none">
                            <path d="M14.5 4H9.3c-.4 0-.7.3-.7.6L6.5 17.2c0 .3.2.5.4.5h2.5c.4 0 .7-.3.7-.6l.6-3.5c0-.3.3-.6.7-.6h1.6c3.3 0 5.2-1.6 5.7-4.8.2-1.4 0-2.5-.6-3.3C17.4 4.3 16.2 4 14.5 4z" fill="rgba(255,255,255,0.7)" />
                            <text x="24" y="17" fill="rgba(255,255,255,0.7)" style={{ fontSize: 14, fontWeight: 600, fontFamily: "system-ui" }}>PayPal</text>
                        </svg>
                    )
                },
                {
                    name: "PostgreSQL", svg: (
                        <svg width="110" height="24" viewBox="0 0 130 24" fill="none">
                            <path d="M16 4c-3.3 0-6 2.7-6 6s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm0 10c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z" fill="rgba(255,255,255,0.7)" />
                            <text x="26" y="17" fill="rgba(255,255,255,0.7)" style={{ fontSize: 14, fontWeight: 600, fontFamily: "system-ui" }}>PostgreSQL</text>
                        </svg>
                    )
                },
            ].map((p, i) => (
                <FadeIn key={p.name} delay={0.08 * i} style={s.partnerItem}>
                    {p.svg}
                </FadeIn>
            ))}
        </div>
    </div>
);

/* ── Styles ── */
const s = {
    // Layout
    page: {
        minHeight: "100vh",
        background: "#16161f",
        color: "rgb(var(--text, 235 235 245))",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        overflowX: "hidden",
    },
    // Nav
    nav: {
        position: "sticky", top: 0, zIndex: 50,
        background: "rgba(12,13,20,0.80)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
    },
    navInner: {
        maxWidth: 1100, margin: "0 auto", padding: "0 26px", minHeight: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
    },
    navLinks: { display: "flex", alignItems: "center", gap: 18 },
    navLink: {
        color: "rgba(255,255,255,0.9)", textDecoration: "none",
        fontSize: 13, fontWeight: 500, background: "none", border: "none", cursor: "pointer",
        padding: 0, margin: 0, transition: "color 0.2s",
    },
    navCta: {
        padding: "7px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
        background: "#6E56CF", border: "none",
        color: "#fff", cursor: "pointer", transition: "all 200ms ease",
    },
    // Hero
    hero: {
        maxWidth: 820, margin: "0 auto", padding: "80px 24px 60px",
        textAlign: "center", position: "relative",
    },
    heroGlow: {
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -55%)",
        width: 600, height: 400,
        background: "radial-gradient(ellipse, rgba(110,86,207,0.15) 0%, transparent 70%)",
        pointerEvents: "none", zIndex: 0,
        animation: "heroGlowPulse 6s ease-in-out infinite",
    },
    heroPill: {
        display: "inline-flex", alignItems: "center", gap: 7,
        background: "rgba(110,86,207,0.10)", border: "1px solid rgba(110,86,207,0.25)",
        borderRadius: 999, padding: "5px 14px", fontSize: 12, color: "#a78bfa",
        marginBottom: 28, fontWeight: 500, position: "relative", zIndex: 1,
    },
    heroTitle: {
        fontSize: "clamp(38px,5.5vw,64px)", fontWeight: 800, lineHeight: 1.06,
        letterSpacing: "-0.04em", margin: "0 0 24px", position: "relative", zIndex: 1,
    },
    heroAccent: { color: "#6E56CF" },
    heroSub: {
        fontSize: 17, color: "rgba(180,180,200,0.75)", maxWidth: 500,
        margin: "0 auto 44px", lineHeight: 1.7, position: "relative", zIndex: 1,
    },
    heroCtas: { display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", position: "relative", zIndex: 1, padding: "0 16px" },
    btnPrimary: {
        padding: "13px 28px", borderRadius: 12, fontSize: 14, fontWeight: 700,
        background: "#6E56CF",
        border: "1px solid rgba(110,86,207,0.8)", color: "#fff", cursor: "pointer",
        boxShadow: "0 8px 28px rgba(110,86,207,0.35), 0 1px 0 rgba(255,255,255,0.10) inset",
        transition: "all 200ms ease",
    },
    btnPrimaryHover: {
        background: "#7c66d9",
        boxShadow: "0 14px 36px rgba(110,86,207,0.45), 0 1px 0 rgba(255,255,255,0.14) inset",
        transform: "translateY(-2px)",
    },
    btnGhost: {
        padding: "13px 28px", borderRadius: 12, fontSize: 14, fontWeight: 600,
        background: "rgba(110,86,207,0.12)",
        border: "1px solid rgba(110,86,207,0.35)",
        color: "rgba(255,255,255,0.9)", cursor: "pointer", transition: "all 200ms ease",
    },
    btnGhostHover: {
        background: "rgba(110,86,207,0.22)",
        border: "1px solid rgba(110,86,207,0.55)",
        transform: "translateY(-1px)",
    },
    // Stats
    stats: {
        maxWidth: 900, margin: "0 auto 40px", padding: "0 16px",
        display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap",
    },
    statItem: {
        flex: "1 1 140px", textAlign: "center", padding: "24px 16px",
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 14,
    },
    statVal: { fontSize: 36, fontWeight: 800, color: "#fff", letterSpacing: "-0.03em" },
    statAccent: { color: "#6E56CF" },
    statLabel: { fontSize: 12, color: "rgba(180,180,200,0.6)", marginTop: 6 },
    // Partners
    partnersSection: {
        maxWidth: 900, margin: "0 auto 80px", padding: "0 24px",
        textAlign: "center",
    },
    partnersLabel: {
        fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase",
        color: "rgba(255,255,255,0.75)", marginBottom: 28,
    },
    partnersGrid: {
        display: "flex", justifyContent: "center", alignItems: "center",
        gap: 24, flexWrap: "wrap",
    },
    partnerItem: {
        opacity: 0.6, transition: "opacity 0.3s",
        cursor: "default",
    },
    // Section
    section: { maxWidth: 1060, margin: "0 auto", padding: "0 24px 100px" },
    sectionLabel: {
        fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
        color: "#6E56CF", marginBottom: 14,
    },
    sectionTitle: {
        fontSize: "clamp(24px,3.5vw,38px)", fontWeight: 800, letterSpacing: "-0.03em",
        margin: "0 0 14px",
    },
    sectionSub: { fontSize: 15, color: "rgba(180,180,200,0.70)", lineHeight: 1.65, maxWidth: 500 },
    // Steps
    stepsGrid: {
        display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
        gap: 14, marginTop: 48,
    },
    stepCard: {
        position: "relative", padding: "30px 24px",
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 16, overflow: "hidden",
        transition: "border-color 0.3s, transform 0.3s",
    },
    stepGlow: {
        position: "absolute", inset: 0, borderRadius: "inherit", pointerEvents: "none",
        background: "radial-gradient(600px 200px at 10% -30%, rgba(110,86,207,0.14), transparent 65%)",
    },
    stepNum: {
        fontSize: 11, fontWeight: 800, letterSpacing: "0.1em",
        color: "rgba(110,86,207,0.65)", marginBottom: 16,
    },
    stepIcon: { fontSize: 28, marginBottom: 16 },
    stepTitle: { fontSize: 15, fontWeight: 700, margin: "0 0 8px" },
    stepDesc: { fontSize: 13, color: "rgba(180,180,200,0.70)", lineHeight: 1.6 },
    // Why
    whyGrid: {
        display: "grid", gridTemplateColumns: "repeat(3,1fr)",
        gap: 14, marginTop: 48,
    },
    whyCard: {
        padding: "26px 22px",
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 16,
        transition: "border-color 0.3s, transform 0.3s",
    },
    whyIcon: { fontSize: 22, marginBottom: 14 },
    whyTitle: { fontSize: 14, fontWeight: 700, margin: "0 0 7px" },
    whyDesc: { fontSize: 13, color: "rgba(180,180,200,0.70)", lineHeight: 1.6 },
    // Pricing
    pricingWrap: { display: "flex", gap: 16, flexWrap: "wrap", marginTop: 48, padding: "0" },
    pricingCard: {
        flex: "1 1 280px", padding: "32px 28px",
        background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 16, position: "relative",
    },
    pricingCardFeatured: {
        flex: "1 1 280px", padding: "32px 28px",
        background: "rgba(110,86,207,0.08)", border: "1px solid rgba(110,86,207,0.30)",
        borderRadius: 16, position: "relative",
        boxShadow: "0 12px 40px rgba(110,86,207,0.15)",
    },
    pricingBadge: {
        position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
        background: "#6E56CF", color: "#fff", fontSize: 11, fontWeight: 700,
        padding: "3px 12px", borderRadius: 999, letterSpacing: "0.06em", whiteSpace: "nowrap",
    },
    pricingName: { fontSize: 13, fontWeight: 600, color: "rgba(180,180,200,0.70)", marginBottom: 10 },
    pricingPrice: { fontSize: 42, fontWeight: 800, letterSpacing: "-0.04em", margin: "0 0 4px" },
    pricingUnit: { fontSize: 13, color: "rgba(180,180,200,0.55)", marginBottom: 22 },
    pricingFeatures: { listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: 10 },
    pricingFeatureItem: { fontSize: 13, color: "rgba(210,210,225,0.85)", display: "flex", alignItems: "center", gap: 8 },
    pricingCheck: { color: "#6E56CF", fontWeight: 800, fontSize: 14 },
    // CTA
    ctaSection: {
        margin: "0 auto 80px", maxWidth: 700, textAlign: "center", padding: "0 24px",
    },
    ctaBox: {
        padding: "64px 40px",
        background: "rgba(110,86,207,0.06)", border: "1px solid rgba(110,86,207,0.20)",
        borderRadius: 20,
        boxShadow: "0 0 100px rgba(110,86,207,0.10)",
    },
    // Footer
    footerWrap: {
        background: "rgba(12,13,20,0.85)", borderTop: "1px solid rgba(255,255,255,0.06)",
        backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
    },
    footerInner: { maxWidth: 1100, margin: "0 auto", padding: "28px 24px 20px" },
};

/* ── CSS animation keyframes ── */
const GlobalStyles = () => (
    <style>{`
        @keyframes heroGlowPulse {
            0%, 100% { opacity: 0.6; transform: translate(-50%, -55%) scale(1); }
            50% { opacity: 1; transform: translate(-50%, -55%) scale(1.15); }
        }
        .step-card-hover:hover {
            border-color: rgba(110,86,207,0.25) !important;
            transform: translateY(-3px);
        }
        .why-card-hover:hover {
            border-color: rgba(110,86,207,0.25) !important;
            transform: translateY(-2px);
        }
        .landing-nav-link:hover {
            color: rgba(255,255,255,0.95) !important;
        }
        .partner-item:hover {
            opacity: 1 !important;
        }

        @media (max-width: 768px) {
            .landing-nav .landing-nav-link {
                display: none !important;
            }
            .landing-nav nav {
                gap: 8px !important;
            }
            .pricing-wrap > div {
                width: 100% !important;
                flex: 1 1 100% !important;
            }
            .why-grid-responsive {
                grid-template-columns: 1fr !important;
            }
        }

        @media (max-width: 600px) {
            section, .stats-wrap {
                padding-left: 16px !important;
                padding-right: 16px !important;
            }
        }
    `}</style>
);


export default function Landing({ onLogin, onNavigate }) {
    return (
        <div style={s.page}>
            <GlobalStyles />

            {/* NAV */}
            <header style={s.nav} className="landing-nav">
                <div style={s.navInner}>
                    <div className="logo" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => onNavigate?.("home")}>
                        <svg width="22" height="22" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="100" cy="100" r="63.75" stroke="#6E56CF" strokeWidth="0.8" strokeOpacity="0.25" fill="none" />
                            <path d="M165 135 A 75 75 0 1 0 135 165" stroke="#6E56CF" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                            <line x1="106.25" y1="100" x2="165" y2="135" stroke="#6E56CF" strokeWidth="0.8" strokeOpacity="0.12" />
                            <line x1="100" y1="106.25" x2="135" y2="165" stroke="#6E56CF" strokeWidth="0.8" strokeOpacity="0.12" />
                            <path d="M165 135 A 75 75 0 0 1 135 165" stroke="#A88FFF" strokeWidth="4" strokeLinecap="round" fill="none" transform="scale(0.92) translate(8.7, 8.7)" />
                            <rect x="93.75" y="93.75" width="12.5" height="12.5" fill="#6E56CF" transform="rotate(45 100 100)" />
                        </svg>
                        <span style={{ fontSize: 18, letterSpacing: "-0.04em", color: "currentColor" }}>
                            Solv<span style={{ color: "#6E56CF", fontWeight: 600 }}>ya</span>
                        </span>
                        <span className="beta-badge" style={{ fontSize: 9, padding: '2px 6px' }}>BETA</span>
                    </div>
                    <nav style={s.navLinks}>
                        {["Comment ça marche", "Pourquoi Solvya", "Tarifs"].map((label, i) => (
                            <a key={i} href={["#how", "#why", "#pricing"][i]} className="landing-nav-link" style={{ ...s.navLink, display: "flex", alignItems: "center", height: 32 }}>
                                {label}
                            </a>
                        ))}
                        <button
                            style={s.navCta}
                            onClick={onLogin}
                            onMouseEnter={e => { e.currentTarget.style.background = "#7c66d9"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(110,86,207,0.4)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "#6E56CF"; e.currentTarget.style.boxShadow = "none"; }}
                        >
                            Commencer →
                        </button>
                    </nav>
                </div>
            </header>

            {/* HERO */}
            <section style={s.hero}>
                <div style={s.heroGlow} />
                <FadeIn delay={0}>
                    <div style={s.heroPill}>
                        <span>⚡</span> Support informatique 100% à distance
                    </div>
                </FadeIn>
                <FadeIn delay={0.1}>
                    <h1 style={s.heroTitle}>
                        Un technicien qualifié<br />
                        <span style={s.heroAccent}>en quelques secondes</span>
                    </h1>
                </FadeIn>
                <FadeIn delay={0.2}>
                    <p style={s.heroSub}>
                        Décrivez votre problème. Un technicien vérifié intervient à distance, immédiatement.
                        Pas de déplacement, paiement à la minute, disponible 24/7.
                    </p>
                </FadeIn>
                <FadeIn delay={0.3}>
                    <div style={s.heroCtas}>
                        <button
                            style={s.btnPrimary}
                            onMouseEnter={e => Object.assign(e.currentTarget.style, s.btnPrimaryHover)}
                            onMouseLeave={e => Object.assign(e.currentTarget.style, s.btnPrimary)}
                            onClick={onLogin}
                        >
                            Demander un technicien
                        </button>
                        <button
                            style={s.btnGhost}
                            onMouseEnter={e => Object.assign(e.currentTarget.style, s.btnGhostHover)}
                            onMouseLeave={e => Object.assign(e.currentTarget.style, s.btnGhost)}
                            onClick={onLogin}
                        >
                            Devenir technicien
                        </button>
                    </div>
                </FadeIn>
            </section>

            {/* STATS */}
            <div style={s.stats}>
                <AnimatedStat value="24" suffix="/7" label="Disponibilité à toute heure" />
                <AnimatedStat value="20" suffix="€" label="À partir de" />
                <AnimatedStat value="100" suffix="%" label="À distance, partout" />
                <AnimatedStat value="0" suffix="€" label="De frais cachés" />
            </div>

            {/* PARTNERS */}
            <PartnerLogos />

            {/* HOW IT WORKS */}
            <section id="how" style={s.section}>
                <FadeIn>
                    <div style={s.sectionLabel}>Comment ça marche</div>
                    <h2 style={s.sectionTitle}>3 étapes, c'est tout.</h2>
                    <p style={s.sectionSub}>
                        De la description du problème à sa résolution, tout se passe en quelques minutes.
                    </p>
                </FadeIn>
                <div style={s.stepsGrid}>
                    {[
                        { num: "01", icon: "💬", title: "Décrivez votre problème", desc: "Votre demande est analysée instantanément pour proposer un diagnostic préliminaire avant même qu'un technicien intervienne." },
                        { num: "02", icon: "🔗", title: "Un technicien prend la main", desc: "Un technicien vérifié accepte votre ticket. Il prend le contrôle de votre écran en un clic, en toute sécurité." },
                        { num: "03", icon: "✅", title: "Problème résolu", desc: "Le problème est résolu. Vous évaluez la prestation. Le paiement est automatique, à la durée réelle d'intervention." },
                    ].map((step, i) => (
                        <FadeIn key={step.num} delay={0.1 * i}>
                            <div className="step-card-hover" style={s.stepCard}>
                                <div style={s.stepGlow} />
                                <div style={s.stepNum}>{step.num}</div>
                                <div style={s.stepIcon}>{step.icon}</div>
                                <h3 style={s.stepTitle}>{step.title}</h3>
                                <p style={s.stepDesc}>{step.desc}</p>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </section>

            {/* WHY */}
            <section id="why" style={s.section}>
                <FadeIn>
                    <div style={s.sectionLabel}>Pourquoi Solvya</div>
                    <h2 style={s.sectionTitle}>Conçu pour aller vite.</h2>
                    <p style={s.sectionSub}>
                        Pas de rendez-vous, pas de déplacement, pas de surprise sur la facture.
                    </p>
                </FadeIn>
                <div style={s.whyGrid} className="why-grid-responsive">
                    {[
                        { icon: "🌍", title: "Partout dans le monde", desc: "Tant qu'il y a une connexion internet, un technicien peut vous aider, peu importe où vous êtes." },
                        { icon: "🔐", title: "Sessions sécurisées", desc: "Chaque session est chiffrée et tracée. Le technicien n'a accès qu'à ce que vous lui montrez." },
                        { icon: "🔍", title: "Diagnostic intelligent", desc: "Votre problème est analysé automatiquement avant l'intervention. Le technicien arrive déjà informé, tout va plus vite." },
                        { icon: "💶", title: "Paiement au juste prix", desc: "À partir de 20€ ou 29€ en Fix Express. Vous payez uniquement le temps réel d'intervention, rien de plus." },
                        { icon: "⭐", title: "Techniciens vérifiés", desc: "Chaque technicien est vérifié, évalué sur ses compétences par Solvya et noté en continu par les clients." },
                        { icon: "⚡", title: "Disponible 24/7", desc: "Notre réseau de techniciens indépendants couvre toutes les plages horaires, y compris les week-ends et nuits." },
                    ].map((item, i) => (
                        <FadeIn key={item.title} delay={0.06 * i}>
                            <div className="why-card-hover" style={s.whyCard}>
                                <div style={s.whyIcon}>{item.icon}</div>
                                <h3 style={s.whyTitle}>{item.title}</h3>
                                <p style={s.whyDesc}>{item.desc}</p>
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </section>

            {/* PRICING */}
            <div style={s.pricingWrap} className="pricing-wrap"></div>
            <section id="pricing" style={s.section}>
                <FadeIn>
                    <div style={s.sectionLabel}>Tarifs</div>
                    <h2 style={s.sectionTitle}>Simple et transparent.</h2>
                    <p style={s.sectionSub}>
                        Pas d'abonnement obligatoire, pas de frais cachés.
                        Vous payez ce que vous utilisez.
                    </p>
                </FadeIn>
                <div style={s.pricingWrap}>
                    <FadeIn delay={0} style={{ flex: '1 1 280px', minWidth: 0 }}>
                        <div style={s.pricingCard}>
                            <div style={s.pricingName}>À la demande</div>
                            <div style={s.pricingPrice}>20€</div>
                            <div style={s.pricingUnit}>10 min incluses, puis 0,75€/min</div>
                            <ul style={s.pricingFeatures}>
                                {["Estimation du coût avant validation", "Paiement au temps réel", "Partage d'écran sécurisé", "Diagnostic intelligent inclus", "Facturation automatique"].map((f) => (
                                    <li key={f} style={s.pricingFeatureItem}>
                                        <span style={s.pricingCheck}>✓</span> {f}
                                    </li>
                                ))}
                            </ul>
                            <button style={s.btnGhost} onClick={onLogin}
                                onMouseEnter={e => Object.assign(e.currentTarget.style, s.btnGhostHover)}
                                onMouseLeave={e => Object.assign(e.currentTarget.style, s.btnGhost)}
                            >Commencer</button>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.06} style={{ flex: '1 1 280px', minWidth: 0 }}>
                        <div style={{ ...s.pricingCard, border: "1px solid rgba(74,222,128,0.20)", background: "rgba(74,222,128,0.03)" }}>
                            <div style={s.pricingName}>Fix Express</div>
                            <div style={s.pricingPrice}>29€</div>
                            <div style={s.pricingUnit}>15 min max, tout compris</div>
                            <ul style={s.pricingFeatures}>
                                {["Prioritaire dans la file d'attente", "Idéal pour les petits soucis", "Diagnostic intelligent inclus", "Partage d'écran sécurisé", "Au-delà : 0,75€/min"].map((f) => (
                                    <li key={f} style={s.pricingFeatureItem}>
                                        <span style={{ ...s.pricingCheck, color: "#4ade80" }}>✓</span> {f}
                                    </li>
                                ))}
                            </ul>
                            <button style={{
                                padding: "13px 28px", borderRadius: 12, fontSize: 14, fontWeight: 600,
                                background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)",
                                color: "#fff", cursor: "pointer", transition: "all 200ms ease",
                            }} onClick={onLogin}
                                onMouseEnter={e => { e.currentTarget.style.background = "rgba(34,197,94,0.22)"; e.currentTarget.style.borderColor = "rgba(34,197,94,0.55)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "rgba(34,197,94,0.12)"; e.currentTarget.style.borderColor = "rgba(34,197,94,0.35)"; e.currentTarget.style.transform = "none"; }}
                            >Commencer</button>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.12} style={{ flex: '1 1 280px', minWidth: 0 }}>
                        <div style={s.pricingCardFeatured}>
                            <div style={s.pricingBadge}>BIENTÔT</div>
                            <div style={s.pricingName}>Abonnement Pro</div>
                            <div style={s.pricingPrice}>—</div>
                            <div style={s.pricingUnit}>tarif entreprise sur devis</div>
                            <ul style={s.pricingFeatures}>
                                {["Tout le plan À la demande", "SLA garanti", "Tableau de bord dédié", "Support prioritaire", "Facturation mensuelle"].map((f) => (
                                    <li key={f} style={s.pricingFeatureItem}>
                                        <span style={s.pricingCheck}>✓</span> {f}
                                    </li>
                                ))}
                            </ul>
                            <button style={{ ...s.btnPrimary, width: "100%", opacity: 0.5, cursor: "not-allowed" }} disabled>
                                Bientôt disponible
                            </button>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* CTA FINAL */}
            <FadeIn>
                <div style={s.ctaSection}>
                    <div style={s.ctaBox}>
                        <h2 style={{ ...s.sectionTitle, marginBottom: 14 }}>
                            Prêt à résoudre votre problème ?
                        </h2>
                        <p style={{ ...s.sectionSub, margin: "0 auto 32px" }}>
                            Un technicien vérifié peut vous aider en quelques secondes.
                        </p>
                        <button
                            style={s.btnPrimary}
                            onMouseEnter={e => Object.assign(e.currentTarget.style, s.btnPrimaryHover)}
                            onMouseLeave={e => Object.assign(e.currentTarget.style, s.btnPrimary)}
                            onClick={onLogin}
                        >
                            Demander un technicien →
                        </button>
                    </div>
                </div>
            </FadeIn>

            {/* FOOTER */}
            <div style={s.footerWrap}>
                <div style={s.footerInner}>
                    <AppFooter onNavigate={onNavigate} />
                </div>
            </div>
            <ChatWidget onNavigate={onNavigate} />
        </div>
    );
}